import {getConcentrationPerMl, getReliableActualDoses} from '@/modules/preparation-label/build-label-data'
import {createLabelFormat, formatLabelDate, formatLabelDateTime} from '@/modules/preparation-label/format'
import type {PreparationLabelData} from '@/modules/preparation-label/types'
import {isVolumetricPreparation} from '@cypro/engine/utils'
import {buildComponentRows, buildComponentTotals} from './components-table'
import type {PreparationReportData, PreparationReportInput, ReportField, ReportTranslate} from './types'
import {isDefined} from '@cypro/ui/utils'

const NO_VALUE = '—'
const SEPARATOR = ' · '

const field = (caption: string, value: string | null | undefined): ReportField => ({caption, value: value || NO_VALUE})

export function buildPreparationReportData(
  input: PreparationReportInput,
  label: PreparationLabelData,
  t: ReportTranslate
): PreparationReportData {
  const {label: labelInput, reportSettings} = input
  const {settings, patient, encounter, substances, carrier} = labelInput
  // numbers and units as on the label, in the organisation's language (CY-403)
  const format = createLabelFormat(settings.language)
  const showCustomer = settings.customerName && Boolean(encounter?.customerLabelName)
  const duration = labelInput.durationMinutes ? `${labelInput.durationMinutes} min` : null

  const patientFields = [
    field(t('preparation_report.patient'), label.patientName),
    field(t('preparation_report.date_of_birth'), isDefined(patient.birthDate) ? formatLabelDate(patient.birthDate) : null),
    // same rule as the label: patients registered to a customer are not identified by the hospital number
    ...(settings.hospitalNumber && !showCustomer
      ? [field(t('preparation_report.hospital_no'), patient.hospitalPatientId)]
      : []),
    field(t('preparation_report.ward'), showCustomer ? encounter?.customerLabelName : encounter?.wardName),
    ...(settings.prescribingDoctor ? [field(t('preparation_report.doctor'), encounter?.doctorName)] : []),
    field(t('preparation_report.administration_day'), formatLabelDate(labelInput.administrationDate)),
  ]

  const drugs = substances
    .map(s => `${s.name} ${format.dose(s.prescribedDose)} ${format.unit(s.doseUnit)}${s.tradeName ? ` (${s.tradeName})` : ''}`)
    .join(', ')
  const routeInCarrier =
    labelInput.routeName && carrier
      ? t('preparation_report.route_in_carrier', {
          route: labelInput.routeName,
          volume: carrier.volumeMl ? format.dose(carrier.volumeMl) : NO_VALUE,
          carrier: carrier.name,
        })
      : (labelInput.routeName ?? carrier?.name ?? null)
  const administration = field(
    t('preparation_report.administration'),
    [drugs, routeInCarrier, duration].filter(Boolean).join(SEPARATOR)
  )

  const prescriptionFields = [
    field(t('preparation_report.storage'), label.storage),
    field(t('preparation_report.best_before'), label.useBy),
    field(t('preparation_report.duration'), duration),
  ]

  const isVolumetric = isVolumetricPreparation(input.preparationType)
  const method = isVolumetric
    ? t('preparation_report.method_volumetric')
    : input.preparationType
      ? t('preparation_report.method_gravimetric')
      : null
  const preparationEnd = field(
    t('preparation_report.preparation_end'),
    input.preparationEnd ? formatLabelDateTime(input.preparationEnd) : null
  )
  const productionFields = [
    field(t('preparation_report.pharmacist_validation'), input.pharmacistValidatedBy),
    field(t('preparation_report.produced_by'), input.producedBy),
    field(
      t('preparation_report.released_by'),
      input.releasedBy && input.releasedAutomatically
        ? t('preparation_report.released_automatically', {name: input.releasedBy})
        : input.releasedBy
    ),
    field(
      t('preparation_report.preparation_start'),
      input.preparationStart ? formatLabelDateTime(input.preparationStart) : null
    ),
    preparationEnd,
    field(t('preparation_report.method'), method),
  ]

  const reliable = getReliableActualDoses(labelInput)
  const concentration = getConcentrationPerMl(labelInput)
  const doseRows = substances.map((substance, index) => {
    const actual = reliable.get(substance.medicineId)
    const row = [
      field(t('preparation_report.target_amount'), `${format.dose(substance.prescribedDose)} ${format.unit(substance.doseUnit)}`),
      field(
        t('preparation_report.actual_amount'),
        actual === undefined ? null : `${format.oneDecimal(actual)} ${format.unit(substance.doseUnit)}`
      ),
      field(
        t('preparation_report.difference'),
        actual === undefined ? null : format.percentDifference(actual, substance.prescribedDose)
      ),
    ]
    if (index > 0) return row
    return [
      ...row,
      field(
        t('preparation_report.total_volume'),
        labelInput.totalVolumeMl ? `${format.volume(labelInput.totalVolumeMl)} ml` : null
      ),
      // the design notes show the concentration only when the preparation has a carrier
      ...(carrier && concentration !== null
        ? [
            field(
              t('preparation_report.concentration'),
              `${format.concentration(concentration)} ${format.perMl(substance.doseUnit)}`
            ),
          ]
        : []),
    ]
  })

  return {
    title: t('preparation_report.title'),
    pharmacyLine: labelInput.pharmacy
      ? [labelInput.pharmacy.name, labelInput.pharmacy.address].filter(Boolean).join(SEPARATOR)
      : null,
    preparationNoCaption: t('preparation_report.preparation_no'),
    preparationNumber: labelInput.productionNumber || NO_VALUE,
    reportDateLine: `${t('preparation_report.report_date')}: ${formatLabelDate(input.reportDate)}`,
    patientFields,
    administration,
    prescriptionFields,
    productionFields,
    doseRows,
    componentColumns: {
      productCode: reportSettings.showProductCodeColumn,
      memoryNumber: reportSettings.showScaleMemoryNumbers && !isVolumetric,
    },
    componentHeaders: {
      type: t('preparation_report.column_type'),
      tradeName: t('preparation_report.column_trade_name'),
      productCode: t('preparation_report.column_product_code'),
      substance: t('preparation_report.column_substance'),
      batchUsage: t('preparation_report.column_batch_usage'),
      quantity: t('preparation_report.column_quantity'),
      memoryNumber: t('preparation_report.column_memory_no'),
    },
    componentRows: buildComponentRows(input, t),
    totals: buildComponentTotals(input, t),
    showLabelCopy: reportSettings.includeLabelCopy,
    showSignatures: reportSettings.includeEndControlSignatures,
    endControl: {
      preparationEnd,
      visualInspection: t('preparation_report.visual_inspection'),
      pharmacistRelease: t('preparation_report.pharmacist_release'),
    },
    sections: {
      patient: t('preparation_report.section_patient'),
      production: t('preparation_report.section_production'),
      dose: t('preparation_report.section_dose'),
      components: t('preparation_report.section_components'),
      label: t('preparation_report.section_label'),
      endControl: t('preparation_report.section_end_control'),
    },
  }
}
